import { useEffect, useRef } from "react";
import { useQueryClient } from "react-query";
import { useStoreState } from "pullstate";
import { useTranslation } from "react-i18next";
import { useAuth } from "@qure/qure-auth";
import Toast from "../Toast";
import ProxyAccountStore from "../../utils/proxyAccountStore";
import { PROXY_ACCESS_QUERY_KEY } from "./useProxyAccess";
import useProxyAccountSession from "./useProxyAccountSession";

/**
 * Runs the proxy session in the background. Renders nothing; mounted once in _app so
 * it works on every route. Restores and revalidates the target (via
 * useProxyAccountSession), and when a grant is revoked mid-session, toasts and drops
 * the user back to their own account.
 *
 * The visible part is the switcher button in the menubar (ProxyAccountSwitcher).
 */
var ProxyAccountSessionManager = function ProxyAccountSessionManager() {
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useAuth = useAuth(),
    refetchProfile = _useAuth.refetchProfile;
  var queryClient = useQueryClient();
  var revocationToken = useStoreState(ProxyAccountStore, function (store) {
    return store.revocationToken;
  });
  var handledRevocationToken = useRef(0);

  // Restores the target after a reload, revalidates it against the profile, and
  // mirrors selections made in other tabs.
  useProxyAccountSession();

  // axiosConnect clears the target; the refetch has to happen here, where
  // refetchProfile is available.
  useEffect(function () {
    if (!revocationToken || revocationToken === handledRevocationToken.current) return;
    handledRevocationToken.current = revocationToken;
    Toast.error(t("account_proxy.revoked", "Proxy access ended - the grant was revoked or expired. You are back on your own account."));
    refetchProfile();
    // The profile is being refetched; keep /access paired with it.
    queryClient.invalidateQueries([PROXY_ACCESS_QUERY_KEY]);
  }, [revocationToken]);
  return null;
};
export default ProxyAccountSessionManager;